import {MxSelectorOrSimply} from '@aviary/store/store.types';
import {MatDialogConfig} from '@angular/material/dialog';
import {Action} from '@ngrx/store';
import {NO_OP} from '@aviary/store/common/common.types';
import {OneOrArrayOf} from '@aviary/store/utility-type.helpers';
import {ComponentType} from '@angular/cdk/portal';

export const DEFAULT_BASE_CANCEL_BUTTON_LABEL: string = 'Cancel';
export const DEFAULT_BASE_CONFIRM_BUTTON_LABEL: string = 'Confirm';

export type DelegateVerticalScrollingPosition =
  'nowhere' | 'above-form' | 'on-descendant-above-form' | 'on-form' | 'on-descendant-of-form' | 'below-form' | 'on-descendant-below-form';

export type ConfirmationCallback = () => void;
export type ValueCalculator<ValueType> = () => ValueType;

export interface BaseDialog<DataType, ValueType> {
  readonly dialogData: DataType;
  readonly dialogDefinition: BaseDialogDefinition<ValueType>;
}

export interface BaseDialogDefinition<ValueType> {
  title: MxSelectorOrSimply<string>;
  description?: MxSelectorOrSimply<string>;
  prompt?: MxSelectorOrSimply<string>;
  cancelButtonLabel?: MxSelectorOrSimply<string>;
  confirmButtonLabel?: MxSelectorOrSimply<string>;
  onConfirm?: ConfirmationCallback;
  valueCalculator?: ValueCalculator<ValueType>;
}

export type DialogCloser<ValueType> = ((value?: ValueType) => void) | typeof NO_OP;
export const ADDED_WHEN_DIALOG_OPENS: DialogCloser<undefined> = NO_OP;

export type ActionCreator<T> = (value: T) => OneOrArrayOf<Action>;

export const BASE_DIALOG_DEFAULT_CONFIG: MatDialogConfig = {
  autoFocus: 'dialog',  // Focus on the dialog element itself, rather than the first element inside (which would be a button for confirmation dialogs).
  restoreFocus: false   // Don't set a focus element after the dialog is closed (which would usually be the button clicked to open the dialog).
};

export type DialogSizeConfig =
  Required<Pick<MatDialogConfig, 'width' | 'minWidth' | 'maxWidth' | 'maxHeight'>>
  & Pick<MatDialogConfig, 'minHeight' | 'position'>;

export type DialogPositionAndHeightConfig =
  Required<Pick<MatDialogConfig, 'position' | 'minHeight' | 'maxHeight'>>;

const NUMERIC_MAX_DIALOG_HEIGHT: number = 95;
const MAX_DIALOG_HEIGHT: string = `${NUMERIC_MAX_DIALOG_HEIGHT}vh`;
export const SMALL_DIALOG_WIDTH: string = '25rem';
export const MEDIUM_DIALOG_WIDTH: string = '37.5rem';
export const LARGE_DIALOG_WIDTH: string = '50rem';
export const EXTRA_LARGE_DIALOG_WIDTH: string = '62.5rem';

// If the current device's screen or window size is smaller than the dialog's defined width, let the dialog take up 100% width, allowing for padding.
export const DEFAULT_DIALOG_WIDTH: string = 'calc(100% - 2rem)';
export const DEFAULT_DIALOG_MIN_WIDTH: string = '0';

export const SMALL_DIALOG_CONFIG: DialogSizeConfig = {
  width: DEFAULT_DIALOG_WIDTH,
  minWidth: DEFAULT_DIALOG_MIN_WIDTH,
  maxWidth: SMALL_DIALOG_WIDTH,
  maxHeight: MAX_DIALOG_HEIGHT
};

export const MEDIUM_DIALOG_CONFIG: DialogSizeConfig = {
  width: DEFAULT_DIALOG_WIDTH,
  minWidth: DEFAULT_DIALOG_MIN_WIDTH,
  maxWidth: MEDIUM_DIALOG_WIDTH,
  minHeight: `min(25rem, ${MAX_DIALOG_HEIGHT})`,
  maxHeight: MAX_DIALOG_HEIGHT
};


// @formatter:off
/**
  Large dialogs (vertically tabbed form dialogs) are given a fixed top position, as opposed to the automatic position based on vertically centering the
  dialog.  This prevents the dialog's position from changing (and the tabs from jumping up and down) as tabs with different content height are activated.
  The maximum height is reduced from our default to allow for this positioning change (so that the dialog doesn't extend past the bottom of the viewport).


  If we want to override this default position and height for a specific large dialog component (perhaps because we know this particular dialog will always
  be taller than most), we can add something like this:

  <pre>
    public static readonly dialogConfigOverrides: MatDialogConfig = {
      ...LARGE_DIALOG_CONFIG,
      ...createLargeDialogConfigPropertiesForPercentageFromTop(5)
    };
  </pre>
*/
// @formatter:on
export const LARGE_DIALOG_CONFIG: DialogSizeConfig = {
  width: DEFAULT_DIALOG_WIDTH,
  minWidth: DEFAULT_DIALOG_MIN_WIDTH,
  maxWidth: LARGE_DIALOG_WIDTH,
  ...createLargeDialogConfigPropertiesForPercentageFromTop(10)
};
export const EXTRA_LARGE_DIALOG_CONFIG: DialogSizeConfig = {
  width: DEFAULT_DIALOG_WIDTH,
  minWidth: DEFAULT_DIALOG_MIN_WIDTH,
  maxWidth: EXTRA_LARGE_DIALOG_WIDTH,
  ...createLargeDialogConfigPropertiesForPercentageFromTop(10)
};

export function createLargeDialogConfigPropertiesForPercentageFromTop(
  percentageFromTop: number,
  percentageFromBottom: number = 100 - NUMERIC_MAX_DIALOG_HEIGHT
): DialogPositionAndHeightConfig {
  const maxHeight: string = `${100 - percentageFromTop - percentageFromBottom}vh`;

  return {
    position: {
      top: `${percentageFromTop}vh`
    },
    minHeight: `min(37.5rem, ${maxHeight})`,
    maxHeight
  };
}

export interface BaseDialogIngredients<DataType, ValueType, DialogComponent> {
  componentClass: ComponentType<DialogComponent>;
  data: DataType;
  onConfirm: ActionCreator<ValueType>;
  onCancel?: ActionCreator<void>;
  onClose?: ActionCreator<void>;
}

export type DialogCancelButtonType = 'text' | 'outlined';

export interface DialogAppConfig {
  cancelButtonType: DialogCancelButtonType;
  showActionsDivider: boolean;
}
